import { kapitelIndex } from './content'
import { allaBegrepp } from './begrepp'
import { allaTeoriposter } from './teorisektion'
import { arbetsomraden, lektionsindex } from './lektionsplanering'

export type SoktypEtikett = 'Kapitel' | 'Begrepp' | 'Modell/teori' | 'Arbetsområde' | 'Lektion'

export interface SokPost {
  typ: SoktypEtikett
  titel: string
  beskrivning: string
  url: string
}

/** Sökindexet byggs av data som redan ligger synkront i huvudbundeln
 *  (kapitelIndex, begrepp, teoriposter, arbetsområden, lektionsindex) — ingen
 *  ny synk, ingen extra nätverksladdning. Sökningen täcker titel + en kort
 *  beskrivning (kapitlens egen sammanfattning, begreppens förklaring,
 *  modellernas kärnpoäng) — INTE kapitlens fulla brödtext, som bara laddas
 *  vid behov (import.meta.glob) för att hålla bundeln liten. Namngivna
 *  modeller/teorier/begrepp täcker i praktiken det mesta ändå: en elev som
 *  söker "Maslow" hittar begreppet och teoriposten även om ordet inte står i
 *  någon kapitelbeskrivning. */
function byggSokindex(): SokPost[] {
  const kapitel: SokPost[] = kapitelIndex
    .filter((k) => k.publicerad && k.slug)
    .map((k) => ({ typ: 'Kapitel', titel: `Kapitel ${k.id}: ${k.titel}`, beskrivning: k.beskrivning, url: `/kapitel/${k.slug}` }))

  const begrepp: SokPost[] = allaBegrepp.map((b) => ({
    typ: 'Begrepp',
    titel: b.begrepp,
    beskrivning: b.forklaring,
    url: `/begrepp/${b.slug}`,
  }))

  const modeller: SokPost[] = allaTeoriposter.map((p) => ({
    typ: 'Modell/teori',
    titel: p.namn,
    beskrivning: p.karnpoang,
    url: `/modeller/${p.teori_id}`,
  }))

  const ao: SokPost[] = arbetsomraden.map((a) => ({
    typ: 'Arbetsområde',
    titel: `Arbetsområde ${a.ordning}: ${a.titel}`,
    beskrivning: `Lärarhandledning och lektionsplanering. ${a.examinationsform}`,
    url: `/lararhandledning/${a.id}`,
  }))

  const lektioner: SokPost[] = lektionsindex.map((l) => ({
    typ: 'Lektion',
    titel: `Lektion ${l.lektionsnummer}: ${l.titel}`,
    beskrivning: `${l.totalMinuter} minuter.`,
    url: `/lararhandledning/${l.arbetsomrade_id}/lektion/${l.lektion_id}`,
  }))

  return [...kapitel, ...begrepp, ...modeller, ...ao, ...lektioner]
}

let index: SokPost[] | null = null
const sokindex = (): SokPost[] => (index ??= byggSokindex())

/** Enkel sökning: kräver att ALLA sökord finns någonstans i titel eller
 *  beskrivning (ingen fuzzy-matchning), och rankar träffar i titeln högre än
 *  träffar bara i beskrivningen. */
export function sok(fraga: string): SokPost[] {
  const ord = fraga
    .toLowerCase()
    .split(/\s+/)
    .map((o) => o.trim())
    .filter(Boolean)
  if (ord.length === 0) return []

  return sokindex()
    .map((post) => {
      const titel = post.titel.toLowerCase()
      const beskrivning = post.beskrivning.toLowerCase()
      const hittade = ord.filter((o) => titel.includes(o) || beskrivning.includes(o))
      if (hittade.length < ord.length) return null
      const poang = ord.reduce((s, o) => s + (titel.includes(o) ? 2 : 1), 0)
      return { post, poang }
    })
    .filter((r): r is { post: SokPost; poang: number } => r !== null)
    .sort((a, b) => b.poang - a.poang)
    .map((r) => r.post)
}
